<template>
  <span v-if="hasVehicle" class="live-passengers-dialog-root" @click.stop>
    <v-tooltip v-if="showTrigger" top>
      <template v-slot:activator="{ on }">
        <span class="live-passengers-dialog__trigger-wrap" v-on="on">
          <v-btn
            fab
            small
            :class="buttonClass"
            :disabled="!resolvedIsLive"
            @click="openDialog"
          >
            <v-icon v-if="!resolvedIsLive" small color="grey">
              fas fa-wifi-slash
            </v-icon>
            <v-icon v-else-if="resolvedPassengerCount === 0" small color="primary">
              fas fa-wifi
            </v-icon>
            <span v-else class="live-passenger-count-label">{{ resolvedPassengerCount }}</span>
          </v-btn>
        </span>
      </template>
      <span>{{ buttonTitle }}</span>
    </v-tooltip>

    <v-dialog
      v-model="dialogOpen"
      scrollable
      max-width="1120px"
      content-class="live-passengers-dialog"
    >
      <v-card>
        <v-card-title class="lighten-2 grey justify-space-between align-center">
          <div>
            <h2 class="mb-0" v-if="vehicleLabel">{{ vehicleLabel }}</h2>
          </div>
          <v-btn icon title="Close" @click="closeDialog">
            <v-icon>far fa-times</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pa-2 live-passengers-dialog__body">
          <div class="live-passengers-dialog__content">
            <div class="live-passengers-dialog__passengers">
              <div v-if="apiInProgress" class="live-passengers-dialog__loading layout column align-center justify-center">
                <live-pulse-indicator status="success" icon="fal fa-users" :size="28" />
                <div class="mt-2"><small>Loading live passengers…</small></div>
              </div>

              <v-alert v-else-if="loadError" type="error" :value="true">
                {{ loadError }}
              </v-alert>

              <div v-else-if="!passengers.length" class="text-xs-center py-4">
                <h3>No boarded passengers</h3>
                <p class="mb-0"><small>This vehicle has no live passengers right now.</small></p>
              </div>

              <div v-else>
                <div class="layout row align-center justify-space-between px-2 pb-2">
                  <strong>{{ passengers.length }} passenger(s)</strong>
                  <!--
                    Passenger counter (APC) from the transport device geo payload
                    (`passengerCount` / livePassengerCount). Not fully integrated yet.
                    <small v-if="typeof resolvedPassengerCount === 'number'">
                      Device count: {{ resolvedPassengerCount }}
                    </small>
                  -->
                </div>
                <div class="live-passengers-dialog__grid">
                  <div
                    v-for="(passenger, index) in passengers"
                    :key="passenger.passengerTripId || passenger.passengerProfileId || index"
                    class="live-passengers-dialog__row"
                  >
                    <div class="live-passengers-dialog__row-main">
                      <div class="live-passengers-dialog__time">{{ passenger.time }}</div>
                      <div class="live-passengers-dialog__identity">
                        <strong>{{ passenger.phoneNumberLabel }}</strong>
                        <div class="live-passengers-dialog__amount">
                          {{ passenger.amountLabel }}
                        </div>
                      </div>
                      <div class="live-passengers-dialog__status">
                        <div
                          class="live-passengers-dialog__icon-circle"
                          :style="{ backgroundColor: passenger.balanceLowThemeColor }"
                          :title="passenger.passengerTripError || (passenger.userPayStatus ? 'Verified' : 'Not verified')"
                        >
                          <v-icon small color="#fff">
                            {{ passenger.userPayStatus ? 'fas fa-check' : 'fas fa-exclamation-triangle' }}
                          </v-icon>
                        </div>
                        <div
                          v-if="passenger.reconfirmCode"
                          class="live-passengers-dialog__reconfirm"
                        >
                          {{ passenger.reconfirmCode }}
                        </div>
                        <div v-if="passenger.prepaid" class="live-passengers-dialog__prepaid">
                          Prepaid<span v-if="passenger.prepaidGuest"> guest</span>
                        </div>
                      </div>
                      <v-btn
                        fab
                        x-small
                        class="ma-0 live-passengers-dialog__profile-btn"
                        title="View Profile"
                        :disabled="!passenger.passengerProfileId"
                        @click.stop="viewPassengerProfile(passenger)"
                      >
                        <v-icon small>fas fa-user</v-icon>
                      </v-btn>
                    </div>
                    <div
                      v-if="hasPassengerWarning(passenger)"
                      class="live-passengers-dialog__row-footer"
                    >
                      <div class="live-passengers-dialog__warnings">
                        <span v-if="passenger.expiresTimestamp" class="live-passengers-dialog__expired">EXPIRED</span>
                        <span v-else-if="!passenger.userPayStatus && passenger.lowBalance">
                          Low balance: {{ passenger.balanceLabel }}
                        </span>
                        <span v-else-if="!passenger.userPayStatus">Not verified</span>
                        <span v-else-if="passenger.extremelyLowBalance">
                          Balance alert: {{ passenger.balanceLabel }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <aside class="live-passengers-dialog__telemetry">
              <div class="live-passengers-dialog__telemetry-header">
                <live-pulse-indicator
                  :status="livePulseStatus"
                  icon="fal fa-location-circle"
                  :size="28"
                  :tooltip="liveStatusLabel"
                />
                <div class="live-passengers-dialog__telemetry-header-text">
                  <strong>{{ liveStatusLabel }}</strong>
                  <div class="live-passengers-dialog__telemetry-subtitle">
                    <small>
                      <span v-if="vehicleLabel" class="live-passengers-dialog__vehicle-label" :title="vehicleLabel">{{ vehicleLabel }} - </span>Vehicle telemetry
                    </small>
                  </div>
                </div>
              </div>

              <div class="live-passengers-dialog__telemetry-row">
                <div class="live-passengers-dialog__telemetry-label"><v-icon small>fas fa-tablet-android-alt</v-icon> Equipment</div>
                <div class="live-passengers-dialog__telemetry-value">
                  {{ equipmentIdLabel }}
                  <div v-if="equipmentLabel"><small>({{ equipmentLabel }})</small></div>
                </div>
              </div>
              <div class="live-passengers-dialog__telemetry-row">
                <div class="live-passengers-dialog__telemetry-label"><v-icon small>{{ vehicleTypeIcon }}</v-icon> Vehicle type</div>
                <div class="live-passengers-dialog__telemetry-value live-passengers-dialog__vehicle-type">
                  <span>{{ vehicleTypeLabel }}</span>
                </div>
              </div>
              <div v-if="lastBusStopLabel" class="live-passengers-dialog__telemetry-row">
                <div class="live-passengers-dialog__telemetry-label">Last bus stop</div>
                <div class="live-passengers-dialog__telemetry-value">{{ lastBusStopLabel }}</div>
              </div>
              <div class="live-passengers-dialog__telemetry-row">
                <div class="live-passengers-dialog__telemetry-label"><v-icon small>fas fa-sign</v-icon> Next bus stop</div>
                <div
                  class="live-passengers-dialog__telemetry-value"
                  :class="{ 'live-passengers-dialog__telemetry-value--unknown': !nextBusStopLabel }"
                >
                  {{ nextBusStopLabel || '(unknown)' }}
                </div>
              </div>
              <div class="live-passengers-dialog__telemetry-row">
                <div class="live-passengers-dialog__telemetry-label">Distance to next stop</div>
                <div
                  class="live-passengers-dialog__telemetry-value"
                  :class="{ 'live-passengers-dialog__telemetry-value--unknown': !distanceToNextStopLabel }"
                >
                  {{ distanceToNextStopLabel || '(unknown)' }}
                </div>
              </div>
              <div class="live-passengers-dialog__telemetry-row">
                <div class="live-passengers-dialog__telemetry-label">Current speed (approx.)</div>
                <div class="live-passengers-dialog__telemetry-value">{{ currentSpeedLabel }}</div>
              </div>
              <div v-if="telemetryInProgress" class="live-passengers-dialog__telemetry-note">
                Loading telemetry…
              </div>
              <div v-else-if="telemetryError" class="live-passengers-dialog__telemetry-note live-passengers-dialog__telemetry-note--error">
                {{ telemetryError }}
              </div>
            </aside>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </span>
</template>

<style scoped>
.live-passengers-dialog__trigger-wrap {
  display: inline-flex;
}

.live-passenger-count-label {
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
}

.live-passengers-dialog__body {
  min-height: 220px;
  max-height: 70vh;
  overflow-y: auto;
}

.live-passengers-dialog__content {
  display: flex;
  align-items: stretch;
  gap: 16px;
}

.live-passengers-dialog__passengers {
  flex: 1;
  min-width: 0;
}

.live-passengers-dialog__telemetry {
  width: 280px;
  flex: 0 0 280px;
  border-left: 1px solid #ddd;
  padding: 4px 8px 4px 16px;
}

.live-passengers-dialog__telemetry-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
  min-width: 0;
}

.live-passengers-dialog__telemetry-header-text {
  flex: 1;
  min-width: 0;
}

.live-passengers-dialog__telemetry-subtitle {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.live-passengers-dialog__telemetry-row {
  border-top: 1px solid #eee;
  padding: 10px 0;
}

.live-passengers-dialog__telemetry-label {
  color: #666;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.live-passengers-dialog__telemetry-value {
  margin-top: 3px;
  font-weight: 600;
}

.live-passengers-dialog__telemetry-value--unknown {
  color: #9e9e9e;
  font-weight: 500;
  font-style: italic;
}

.live-passengers-dialog__vehicle-type {
  display: flex;
  align-items: center;
  gap: 6px;
}

.live-passengers-dialog__telemetry-note {
  color: #666;
  font-size: 12px;
  font-style: italic;
  margin-top: 12px;
}

.live-passengers-dialog__telemetry-note--error {
  color: #c62828;
}

.live-passengers-dialog__loading {
  min-height: 180px;
}

.live-passengers-dialog__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0;
}

.live-passengers-dialog__row {
  border: 1px solid #b2b2b2;
  border-width: 0 1px 1px 0;
  padding: 6px 8px;
}

.live-passengers-dialog__row:nth-child(2n) {
  border-right-width: 0;
}

.live-passengers-dialog__row-main {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
}

.live-passengers-dialog__time {
  min-width: 56px;
  font-weight: 600;
  font-size: 13px;
}

.live-passengers-dialog__identity {
  flex: 1;
  min-width: 0;
  line-height: 1.25;
}

.live-passengers-dialog__amount {
  color: #000;
  margin-top: 1px;
  font-size: 13px;
}

.live-passengers-dialog__status {
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-shrink: 0;
  gap: 6px;
}

.live-passengers-dialog__icon-circle {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.live-passengers-dialog__prepaid {
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
}

.live-passengers-dialog__reconfirm {
  min-width: 34px;
  height: 28px;
  padding: 0 6px;
  background: #1f4ba5;
  color: #fff;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 12px;
  text-transform: uppercase;
  flex-shrink: 0;
}

.live-passengers-dialog__profile-btn {
  flex-shrink: 0;
  width: 28px !important;
  height: 28px !important;
  min-width: 28px !important;
  margin: 0 !important;
}

.live-passengers-dialog__row-footer {
  display: flex;
  align-items: center;
  margin-top: 4px;
}

.live-passengers-dialog__warnings {
  font-size: 11px;
  font-style: italic;
  font-weight: 600;
  color: #666;
}

.live-passengers-dialog__expired {
  color: #c62828;
  text-transform: uppercase;
  font-style: normal;
}

@media (max-width: 700px) {
  .live-passengers-dialog__content {
    display: block;
  }

  .live-passengers-dialog__telemetry {
    width: auto;
    border-left-width: 0;
    border-top: 1px solid #ddd;
    margin-top: 16px;
    padding: 16px 4px 4px;
  }

  .live-passengers-dialog__grid {
    grid-template-columns: 1fr;
  }

  .live-passengers-dialog__row:nth-child(2n) {
    border-right-width: 1px;
  }
}
</style>

<script>
import src from './src'
export default src
</script>
